import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, Users, Eye, TrendingUp, Monitor, Smartphone, Tablet, RefreshCw } from 'lucide-react';
import { api } from '../utils/api';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [trafficTrend, setTrafficTrend] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [deviceBreakdown, setDeviceBreakdown] = useState([]);
  const [realtimeUsers, setRealtimeUsers] = useState(0);
  const [error, setError] = useState(null);

  const COLORS = ['#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all analytics data in parallel
      const [overviewRes, trendRes, pagesRes, sourcesRes, devicesRes, realtimeRes] = await Promise.all([
        api.request('/analytics/overview'),
        api.request('/analytics/traffic-trend?days=30'),
        api.request('/analytics/top-pages?limit=10'),
        api.request('/analytics/traffic-sources'),
        api.request('/analytics/device-breakdown'),
        api.request('/analytics/realtime')
      ]);

      if (overviewRes && overviewRes.success) {
        setOverview(overviewRes.data);
      }

      if (trendRes && trendRes.success) {
        setTrafficTrend(trendRes.data);
      }

      if (pagesRes && pagesRes.success) {
        setTopPages(pagesRes.data);
      }

      if (sourcesRes && sourcesRes.success) {
        setTrafficSources(sourcesRes.data);
      }

      if (devicesRes && devicesRes.success) {
        setDeviceBreakdown(devicesRes.data);
      }

      if (realtimeRes && realtimeRes.success) {
        setRealtimeUsers(realtimeRes.data.activeUsers);
      }

    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data. This might be a new property with no data yet.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();

    // Refresh realtime data every 30 seconds
    const interval = setInterval(async () => {
      try {
        const res = await api.request('/analytics/realtime');
        if (res && res.success) {
          setRealtimeUsers(res.data.activeUsers);
        }
      } catch (err) {
        console.error('Error refreshing realtime data:', err);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const getDeviceIcon = (device) => {
    switch (device.toLowerCase()) {
      case 'mobile': return <Smartphone className="w-5 h-5" />;
      case 'tablet': return <Tablet className="w-5 h-5" />;
      case 'desktop': return <Monitor className="w-5 h-5" />;
      default: return <Monitor className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-3 text-lg">Loading Analytics...</span>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-800">{error}</p>
        <button
          onClick={fetchAnalyticsData}
          className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Google Analytics Dashboard</h2>
          <p className="text-gray-600 mt-1">Last 30 days performance overview</p>
        </div>
        <button
          onClick={fetchAnalyticsData}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Realtime Users */}
      <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Active Users Right Now</p>
              <p className="text-4xl font-bold mt-2">{realtimeUsers}</p>
            </div>
            <Activity className="w-16 h-16 opacity-50" />
          </div>
        </CardContent>
      </Card>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-3xl font-bold mt-1">{formatNumber(overview.activeUsers)}</p>
                </div>
                <Users className="w-10 h-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Sessions</p>
                  <p className="text-3xl font-bold mt-1">{formatNumber(overview.sessions)}</p>
                </div>
                <Activity className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Page Views</p>
                  <p className="text-3xl font-bold mt-1">{formatNumber(overview.pageViews)}</p>
                </div>
                <Eye className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Bounce Rate</p>
                  <p className="text-3xl font-bold mt-1">{overview.bounceRate.toFixed(1)}%</p>
                </div>
                <TrendingUp className="w-10 h-10 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Traffic Trend Chart */}
      {trafficTrend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Traffic Trend (30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trafficTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="users" stroke="#7c3aed" strokeWidth={2} name="Users" />
                <Line type="monotone" dataKey="sessions" stroke="#ec4899" strokeWidth={2} name="Sessions" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        {topPages.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Top Pages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topPages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{page.title || page.path}</p>
                      <p className="text-sm text-gray-500 truncate">{page.path}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <p className="font-bold text-purple-600">{formatNumber(page.views)}</p>
                      <p className="text-xs text-gray-500">{formatNumber(page.users)} users</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Device Breakdown */}
        {deviceBreakdown.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Device Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={deviceBreakdown}
                    dataKey="users"
                    nameKey="device"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {deviceBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {deviceBreakdown.map((device, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      {getDeviceIcon(device.device)}
                      <span className="font-medium capitalize">{device.device}</span>
                    </div>
                    <span className="text-sm text-gray-600">{formatNumber(device.users)} users</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Traffic Sources */}
      {trafficSources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Traffic Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={trafficSources}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="source" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="sessions" fill="#7c3aed" name="Sessions" />
                <Bar dataKey="users" fill="#ec4899" name="Users" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* No Data Message */}
      {!overview && !error && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <Activity className="w-16 h-16 mx-auto text-blue-600 mb-4" />
              <h3 className="text-xl font-bold mb-2">No Data Yet</h3>
              <p className="text-gray-600">
                Your GA4 property is set up correctly, but hasn't collected any data yet.
                <br />
                Start getting traffic to see your analytics here!
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
